import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supplierService, productService } from '../lib/firebaseService';
import { Truck, Plus, Phone, Mail, Edit, Trash2, ShoppingCart, Loader2, Copy, Check } from 'lucide-react';
import { useForm } from 'react-hook-form';
import '../styles/components-extended.css';
import { t } from '../utils/translation';
import SupplierModal from '../components/SupplierModal';

export default function Suppliers() {
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [generatedOrder, setGeneratedOrder] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const { data: suppliers, isLoading } = useQuery({ queryKey: ['suppliers'], queryFn: supplierService.getSuppliers });
    // We need products to check stock levels for ordering
    const { data: products } = useQuery({ queryKey: ['products'], queryFn: productService.getProducts });

    const deleteMutation = useMutation({
        mutationFn: supplierService.deleteSupplier,
        onSuccess: () => queryClient.invalidateQueries(['suppliers'])
    });

    const handleEdit = (supplier) => {
        setEditingSupplier(supplier);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('COMMON.CONFIRM'))) {
            await deleteMutation.mutateAsync(id);
        }
    };

    const handleGenerateOrder = async (supplier) => {
        setIsGenerating(true);
        setGeneratedOrder(null);

        // 1. Filter products for this supplier that are low on stock
        const supplierProducts = products?.filter(p => p.supplierId === supplier.id) || [];
        const lowStockProducts = supplierProducts.filter(p => p.stock < (p.lowStockThreshold || 10));

        if (lowStockProducts.length === 0) {
            alert(t('COMMON.NO_DATA'));
            setIsGenerating(false);
            return;
        }

        // 2. Format the message
        let message = `Bonjour ${supplier.contactName || ''},\n\n`;
        message += `Nous souhaitons passer commande pour les articles suivants :\n\n`;

        lowStockProducts.forEach(p => {
            const reorderQty = (p.reorderQty || 20) - p.stock; // Simple logic: fill up to 20 or custom
            // Use a safer default logic: Order enough to reach threshold + buffer, e.g. order 10
            const qtyToOrder = Math.max(10, (p.lowStockThreshold || 10) * 2 - p.stock);

            message += `- ${p.brand} ${p.name}: ${qtyToOrder} unités\n`;
        });

        message += `\nMerci,\nInventaire U`;

        setGeneratedOrder({
            supplierName: supplier.name,
            text: message
        });
        setIsGenerating(false);
    };

    return (
        <div className="max-w-[1200px] mx-auto">
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('SUPPLIERS.TITLE')}</h1>
                    <p className="page-subtitle">{t('SUPPLIERS.SUBTITLE')}</p>
                </div>
                <button
                    onClick={() => { setEditingSupplier(null); setIsModalOpen(true); }}
                    className="btn btn-primary"
                >
                    <Plus size={18} className="mr-2" />
                    {t('SUPPLIERS.ADD_SUPPLIER')}
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center p-12"><Loader2 className="animate-spin text-purple-600" /></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {suppliers?.map(supplier => (
                        <div key={supplier.id} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center">
                                    <div className="bg-blue-50 p-2.5 rounded-lg text-blue-600 mr-3">
                                        <Truck size={20} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900">{supplier.name}</h3>
                                        <p className="text-sm text-gray-500">{supplier.contactName}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => handleEdit(supplier)} className="p-1.5 text-gray-400 hover:text-purple-600 rounded-md hover:bg-purple-50 transition-colors">
                                        <Edit size={16} />
                                    </button>
                                    <button onClick={() => handleDelete(supplier.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2 mb-6 hidden-mobile">
                                {supplier.email && (
                                    <div className="flex items-center text-sm text-gray-600 truncate">
                                        <Mail size={14} className="mr-2 text-gray-400" />
                                        {supplier.email}
                                    </div>
                                )}
                                {supplier.phone && (
                                    <div className="flex items-center text-sm text-gray-600">
                                        <Phone size={14} className="mr-2 text-gray-400" />
                                        {supplier.phone}
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => handleGenerateOrder(supplier)}
                                className="w-full btn btn-secondary text-blue-700 bg-blue-50 border-blue-100 hover:bg-blue-100"
                            >
                                <ShoppingCart size={16} className="mr-2" />
                                {t('SUPPLIERS.GENERATE_ORDER')}
                            </button>
                        </div>
                    ))}

                    {(!suppliers || suppliers.length === 0) && (
                        <div className="col-span-full text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <Truck size={48} className="mx-auto text-gray-300 mb-3" />
                            <h3 className="text-gray-900 font-medium">{t('SUPPLIERS.NO_SUPPLIERS')}</h3>
                            <p className="text-gray-500 text-sm">{t('SUPPLIERS.NO_SUPPLIERS_DESC')}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Order Preview Modal (Simple Inline for now) */}
            {generatedOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-bold text-xl text-gray-900">{t('SUPPLIERS.ORDER_MODAL.TITLE')} {generatedOrder.supplierName}</h3>
                            <button onClick={() => setGeneratedOrder(null)} className="text-gray-400 hover:text-gray-600">×</button>
                        </div>
                        <div className="p-6 bg-gray-50">
                            <textarea
                                readOnly
                                className="w-full h-64 p-4 text-sm font-mono text-gray-700 bg-white border border-gray-200 rounded-xl focus:outline-none resize-none"
                                value={generatedOrder.text}
                            />
                        </div>
                        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
                            <button onClick={() => setGeneratedOrder(null)} className="btn btn-secondary">{t('COMMON.CLOSE')}</button>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(generatedOrder.text);
                                    alert(t('COMMON.COPY') + '!');
                                }}
                                className="btn btn-primary bg-blue-600 border-blue-600 hover:bg-blue-700 hover:border-blue-700"
                            >
                                <Copy size={16} className="mr-2" />
                                {t('SUPPLIERS.ORDER_MODAL.COPY_TEXT')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SupplierModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                supplier={editingSupplier}
            />
        </div>
    );
}
